// ParticleBackground.tsx
import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { useTheme } from '../context/ThemeContext'

const ParticleBackground: React.FC = () => {
  const { theme } = useTheme();
  
  const particles = useMemo(() => {
    // Enhanced color palettes with more vibrant light theme colors
    const darkThemeColors = ['#4B7BFF', '#8E44AD', '#6C5CE7', '#00CEC9'];
    const lightThemeColors = ['#4B7BFF', '#8E44AD', '#7B68EE', '#3498DB', '#9B59B6'];
    
    const colors = theme === 'dark' ? darkThemeColors : lightThemeColors;
    
    return Array.from({ length: 40 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      color: colors[Math.floor(Math.random() * colors.length)],
    }))
  }, [theme])

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            opacity: theme === 'dark' ? 0.3 : 0.15,
            filter: theme === 'light' ? 'blur(0.5px)' : 'none',
          }}
          animate={{
            x: [particle.x - 10, particle.x + 10, particle.x - 10],
            y: [particle.y - 10, particle.y + 10, particle.y - 10],
          }}
          transition={{
            duration: Math.random() * 10 + 10,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
      
      {/* Add larger blurred gradient blobs for background depth */}
      <motion.div
        className={`absolute w-96 h-96 rounded-full ${theme === 'dark' 
          ? 'bg-[#4B7BFF]/10' 
          : 'bg-gradient-to-r from-blue-300/10 to-indigo-300/5'} blur-3xl`}
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          left: '15%',
          top: '20%',
        }}
      />
      
      <motion.div
        className={`absolute w-[30rem] h-[30rem] rounded-full ${theme === 'dark' 
          ? 'bg-[#8E44AD]/10' 
          : 'bg-gradient-to-r from-purple-300/10 to-pink-300/5'} blur-3xl`}
        animate={{
          x: [100, 0, 100],
          y: [50, 0, 50],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear"
        }}
        style={{
          right: '10%',
          top: '50%',
        }}
      />
      
      {/* Light theme extra blob */}
      {theme === 'light' && (
        <motion.div
          className="absolute w-[25rem] h-[25rem] rounded-full bg-gradient-to-r from-cyan-200/5 to-blue-200/10 blur-3xl"
          animate={{
            x: [-50, 50, -50],
            y: [30, -30, 30],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            left: '40%',
            top: '70%',
          }}
        />
      )}
    </div>
  )
}

export default ParticleBackground
